import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

// The signature replacement device. Two layers (from and to) are both always
// present and crossfade via opacity/transform only, so the first paint always
// shows `from` with no flash of the `to` text. Phase machine:
//   idle: from visible, no line.
//   striking: line draws left to right across from (0.35s), text unchanged.
//   swapped: line vanishes instantly, from fades up, to fades in.
// Deactivation crossfades back to from with no reverse strike.
export function StrikeSwap({ from, to, active, className, testid }) {
    const [phase, setPhase] = useState("idle");
    const timer = useRef(null);

    useEffect(() => {
        clearTimeout(timer.current);
        if (active) {
            setPhase("striking");
            timer.current = setTimeout(() => setPhase("swapped"), 350);
        } else {
            setPhase("idle");
        }
        return () => clearTimeout(timer.current);
    }, [active]);

    const swapped = phase === "swapped";
    const striking = phase === "striking";
    const spacer = from.length >= to.length ? from : to;

    return (
        <span data-testid={testid} className={cn("relative inline-block", className)}>
            {/* invisible spacer reserves the box for the longer of the two strings */}
            <span aria-hidden className="invisible whitespace-nowrap">{spacer}</span>

            {/* from layer */}
            <motion.span
                className="absolute inset-0 flex items-center text-muted-foreground"
                initial={false}
                animate={{ opacity: swapped ? 0 : 1, y: swapped ? -8 : 0 }}
                transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            >
                <span className="relative inline-block">
                    {from}
                    <motion.span
                        aria-hidden
                        className="absolute left-0 top-1/2 h-[2px] w-full origin-left rounded-full bg-current"
                        initial={{ scaleX: 0, opacity: 0 }}
                        animate={{ scaleX: striking ? 1 : 0, opacity: striking ? 1 : 0 }}
                        transition={striking ? { duration: 0.35, ease: "easeInOut" } : { duration: 0 }}
                        style={{ translateY: "-50%" }}
                    />
                </span>
            </motion.span>

            {/* to layer */}
            <motion.span
                className="absolute inset-0 flex items-center text-foreground"
                initial={false}
                animate={{ opacity: swapped ? 1 : 0, y: swapped ? 0 : 8 }}
                transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            >
                {to}
            </motion.span>
        </span>
    );
}
